import { request, uploadWithProgress } from './client'
import type { UploadProgress } from './client'
import type {
  AnswerResponse,
  AnswerValue,
  ApiHealth,
  EventInfo,
  GameState,
  Me,
  PhotoResponse,
  RegistrationInput,
  Results,
  VerificationInfo,
  VerificationInput,
} from './types'

export type * from './types'
export { ApiError } from './client'

export const api = {
  health: () => request<ApiHealth>('/api/health'),

  event: (slug: string) => request<{ event: EventInfo }>(`/api/events/${encodeURIComponent(slug)}`),

  register: (slug: string, input: RegistrationInput) =>
    request<Me>(`/api/events/${encodeURIComponent(slug)}/registrations`, { method: 'POST', body: input }),

  /**
   * New-device resume: exchange the emailed code for a session. Rejects with
   * ApiError(401, OTP_INVALID | OTP_EXPIRED | OTP_LOCKED).
   */
  verify: (slug: string, input: VerificationInput) =>
    request<Me>(`/api/events/${encodeURIComponent(slug)}/verifications`, { method: 'POST', body: input }),

  /** Ask for a fresh code (at most one per minute; the response never reveals whether the email exists). */
  resendCode: (slug: string, email: string) =>
    request<{ verification: VerificationInfo }>(`/api/events/${encodeURIComponent(slug)}/verifications/resend`, {
      method: 'POST',
      body: { email },
    }),

  /** Who am I? Rejects with ApiError(401, UNAUTHENTICATED) when there is no valid session. */
  me: () => request<Me>('/api/me'),

  logout: () => request<void>('/api/session', { method: 'DELETE' }),

  /** Authoritative game state; the recovery endpoint after any doubt. */
  game: () => request<{ game: GameState }>('/api/game'),

  answer: (puzzleId: string, value: AnswerValue) =>
    request<AnswerResponse>('/api/game/answers', { method: 'POST', body: { puzzleId, value } }),

  /** Reveal a hint (charges its penalty once). Returns the new game state. */
  useHint: (puzzleId: string, hintId: string) =>
    request<{ game: GameState }>(`/api/puzzles/${encodeURIComponent(puzzleId)}/hints/${encodeURIComponent(hintId)}/use`, {
      method: 'POST',
    }),

  /** Final results; 409 RUN_NOT_FINISHED until the run is finished. */
  results: () => request<{ results: Results }>('/api/results'),

  /** Non-answer interaction on the current puzzle (e.g. "start" a reveal or timed task). */
  act: (puzzleId: string, action: string) =>
    request<{ game: GameState }>('/api/game/actions', { method: 'POST', body: { puzzleId, action } }),

  /** Uploads a photo for the current photo puzzle; completion depends on the server accepting it. */
  uploadPhoto: (puzzleId: string, file: File, onProgress?: UploadProgress) => {
    const form = new FormData()
    form.append('puzzleId', puzzleId)
    form.append('photo', file)
    return uploadWithProgress<PhotoResponse>('/api/game/photo', form, onProgress)
  },
}
